import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api'; 

// --- NEW COMPONENT: Individual Profile Row ---
// We create this to isolate the Follow state for each user in the list
const ProfileRow = ({ profile, setCurrentView, setViewedUserId }) => {
  const [isFollowing, setIsFollowing] = useState(profile.is_following || false);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleFollowToggle = async () => {
    setIsFollowLoading(true);
    try {
      // We use your 'api' instance and put the user_id directly into the URL path 
      // matching your router.route('/:targetUserId/follow')
      await api.post(`/follows/${profile.user_id}/follow`);
      
      // Flip the status visually after a successful API call
      setIsFollowing(!isFollowing); 
    } catch (error) {
      console.error("Failed to toggle follow", error);
    } finally {
      setIsFollowLoading(false);
    }
  };


  return (
    <div 
      className="sug-item profile-row" 
      style={{ cursor: 'pointer', padding: '10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '15px' }}
      onClick={() => {
        // Restored: Allows you to click the row to visit the profile
        const targetIdentifier = profile.handle || profile.user_id || profile.id;
        setViewedUserId(targetIdentifier); 
        setCurrentView('profile');    
      }}
    >
      {/* Avatar (Restored inline styles to prevent massive images) */}
      <div 
        className="sug-av profile-avatar" 
        style={{ 
          background: 'var(--amber-l)', 
          width: '40px', 
          height: '40px', 
          borderRadius: '50%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          fontSize: '18px', 
          overflow: 'hidden',
          flexShrink: 0 // This prevents the flexbox from squishing the image
        }}
      >
        {profile.avatar_url ? (
          <img src={profile.avatar_url} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          profile.display_name ? profile.display_name.charAt(0).toUpperCase() : 'U'
        )}
      </div>

      {/* Name, Role/Location, and Tags */}
      <div className="profile-info" style={{ flex: 1 }}>
        <div className="sug-name" style={{ fontWeight: '600', fontSize: '12px', color: 'var(--text)' }}>
          {profile.display_name} {profile.is_verified && '✦'}
        </div>
        
        <div className="sug-role" style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '4px' }}>
          @{profile.handle} {profile.location_text && `· ${profile.location_text}`}
        </div>

        {/* The Creative Types / Tags */}
        <div className="tags" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {profile.tags && profile.tags.filter(Boolean).map((tag, i) => (
            <span key={i} className="tag tg-p" style={{ fontSize: '11px' }}>{tag}</span>
          ))}
        </div>
      </div>

      {/* Follow / Unfollow Button */}
      <div className="profile-actions">
        <button 
          className={`sug-follow ${isFollowing ? 'following' : ''}`}
          onClick={(e) => {
            e.stopPropagation(); // IMPORTANT: Prevents clicking 'Follow' from also opening the profile
            handleFollowToggle();
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          disabled={isFollowLoading}
          style={{
            background: isFollowing ? 'transparent' : 'var(--text)',
            color: isFollowing ? 'var(--text)' : 'var(--bg)',
            border: isFollowing ? '1px solid var(--border)' : 'none',
            padding: '5px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: isFollowLoading ? 'not-allowed' : 'pointer',
            opacity: isFollowLoading ? 0.7 : 1,
            transition: 'all 0.2s ease',
            ...(isFollowing && isHovered ? {
              color: '#ff4d4f',
              borderColor: '#ff4d4f',
              background: 'rgba(255, 77, 79, 0.1)'
            } : {})
          }}
        >
          {isFollowLoading 
            ? '...' 
            : isFollowing 
              ? (isHovered ? 'Unfollow' : 'Following') 
              : 'Follow'}
        </button>
      </div>
    </div>
  );
};


export default function DiscoverView({ setCurrentView, setActiveModal, setViewedUserId }) {
  // 1. Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState('All');
  const [activeMarket, setActiveMarket] = useState('All');
  const [activeStatuses, setActiveStatuses] = useState([]);
  
  // 2. Data & Pagination State
  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // 3. Infinite Scroll Observer
  const observer = useRef();
  const lastProfileElementRef = useCallback(node => {
      if (isLoading) return;
      
      if (observer.current) observer.current.disconnect();
      
      observer.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && hasMore) {
              setPage(prevPage => prevPage + 1);
          }
      });
      
      if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Handle status toggling
  const toggleStatus = (status) => {
    setActiveStatuses(prevStatuses => {
      if (prevStatuses.includes(status)) {
        return prevStatuses.filter(s => s !== status);
      } else {
        return [...prevStatuses, status];
      }
    });
  };

  // 4. Reset pagination when any filter changes
  useEffect(() => {
    setProfiles([]); // Clear current list
    setPage(1);      // Reset to page 1
    setHasMore(true);
  }, [searchQuery, activeType, activeMarket, activeStatuses]);

  // 5. Unified Fetch Logic (Handles both Filters & Pagination)
  useEffect(() => {
    const fetchDiscovery = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.append('q', searchQuery);
        if (activeType !== 'All') params.append('type', activeType);
        if (activeMarket !== 'All') params.append('market', activeMarket);
        
        if (activeStatuses.length > 0) {
          params.append('statuses', activeStatuses.join(','));
        }
        
        // Append pagination limits
        params.append('page', page);
        params.append('limit', 20);

        const res = await api.get(`/profiles/search?${params.toString()}`);
        const fetchedProfiles = res.data.profiles || [];
        
        // If it's page 1, replace the array. Otherwise, append to it.
        setProfiles(prevProfiles => {
          if (page === 1) return fetchedProfiles;
          return [...prevProfiles, ...fetchedProfiles];
        });
        
        // If we got fewer than 20 items, there's no more data to fetch
        setHasMore(fetchedProfiles.length === 20);
      } catch (err) {
        console.error('Error fetching discovery feed:', err);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the fetch to prevent spamming the API while typing
    const delayDebounceFn = setTimeout(() => {
      fetchDiscovery();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, activeType, activeMarket, activeStatuses, page]);

  return (
    <div className="view active" id="view-discover">
      
      {/* Search Bar */}
      <div style={{ marginBottom: '15px' }}>
        <input 
          type="text" 
          className="setting-select" 
          placeholder="Search by name, handle, or bio..." 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ width: '100%', padding: '10px 15px' }}
        />
      </div>

      {/* Filters UI */}
      <div className="discover-filters">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', fontFamily: "'Syne', sans-serif" }}>Browse Creatives</span>
          <button className="btn btn-sm" onClick={() => setActiveModal('custom-filter-modal')}>⚙ Custom filter</button>
        </div>
        
        <div className="filter-row">
          <span className="filter-lbl">Type</span>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {['All', 'Designers', 'Musicians', 'Photographers', 'Actors'].map(type => (
              <div key={type} className={`chip ${activeType === type ? 'active' : ''}`} onClick={() => setActiveType(type)}>{type}</div>
            ))}
          </div>
        </div>
        
        <div className="filter-row">
          <span className="filter-lbl">Market</span>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {['All', 'Nairobi', 'Lagos', 'Accra'].map(market => (
              <div key={market} className={`chip ${activeMarket === market ? 'active' : ''}`} onClick={() => setActiveMarket(market)}>{market}</div>
            ))}
          </div>
        </div>
        
        <div className="filter-row">
          <span className="filter-lbl">Status</span>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {['✦ Verified', 'Open to work', 'Top rated'].map(status => (
              <div key={status} className={`chip ${activeStatuses.includes(status) ? 'active' : ''}`} onClick={() => toggleStatus(status)}>{status}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="discover-panel">
      {/* Render the smaller suggested cards instead of the grid */}
      <div className="suggested-card" style={{ marginTop: '20px', padding: '0', background: 'transparent', border: 'none' }}>
        {profiles.length > 0 ? (
          profiles.map((profile, index) => {
            const isLastElement = profiles.length === index + 1;
            
            return isLastElement ? (
              <div ref={lastProfileElementRef} key={profile.user_id || index}>
                <ProfileRow 
                  profile={profile} 
                  setCurrentView={setCurrentView} 
                  setViewedUserId={setViewedUserId} 
                />
              </div>
            ) : (
              <ProfileRow 
                key={profile.user_id || index} 
                profile={profile} 
                setCurrentView={setCurrentView} 
                setViewedUserId={setViewedUserId} 
              />
            );
          })
        ) : !isLoading ? (
          <div style={{ color: 'var(--text3)', padding: '20px' }}>No creatives found matching those filters.</div>
        ) : null}
      </div>
      </div>
      
      {/* Loading & End Indicators */}
      {isLoading && <div className="spinner" style={{ color: 'var(--text3)', marginTop: '20px' }}>Loading creatives...</div>}
      {!hasMore && profiles.length > 0 && <div className="end-message" style={{ color: 'var(--text3)', marginTop: '20px', textAlign: 'center' }}>You've reached the edge of the Buniverse!</div>}
      
    </div>
  );
}